import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatErr } from "../lib/api";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { AutoGlowBrand } from "./components/AutoGlowLogo";
import {
  Sparkle, CreditCard, CheckCircle, Clock, Warning, ArrowRight, Crown,
} from "@phosphor-icons/react";
import { toast } from "sonner";

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("es-ES", { day: "2-digit", month: "short", year: "numeric" }); } catch { return iso; }
}

const STATUS_LABEL = {
  trialing: { label: "Prueba gratuita activa", tone: "bg-amber-100 text-amber-900", icon: Clock },
  active: { label: "Suscripción activa", tone: "bg-emerald-100 text-emerald-900", icon: CheckCircle },
  trial_expired: { label: "Prueba expirada — paga para reactivar", tone: "bg-red-100 text-red-900", icon: Warning },
  past_due: { label: "Pago pendiente", tone: "bg-red-100 text-red-900", icon: Warning },
  canceled: { label: "Cancelada", tone: "bg-gray-200 text-gray-900", icon: Warning },
  lifetime: { label: "Lifetime · acceso permanente", tone: "bg-[var(--ag-gold)]/20 text-[var(--ag-gold)]", icon: Crown },
  none: { label: "Sin suscripción", tone: "bg-gray-200 text-gray-900", icon: Warning },
};

const FEATURE_LIST = [
  { key: "collaborators", label: "Equipo de colaboradores (autónomos)" },
  { key: "team_invites", label: "Invitaciones y roles (Gestor)" },
  { key: "irpf_split", label: "Reparto IRPF + colaboradores" },
  { key: "dir3_billing", label: "Facturación DIR3 sector público" },
  { key: "vouchers", label: "Bonos prepagados / vales de servicio" },
  { key: "expense_attachments", label: "Gastos avanzados con adjuntos PDF/IMG" },
  { key: "tax_models_aeat", label: "Modelos AEAT (303, 111)" },
  { key: "marketplace_premium", label: "Destacado en Marketplace internacional" },
];

export default function Billing() {
  useNavigate();
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/billing/status");
      setStatus(data);
      setSelectedPlan(data.subscription_plan || "agenda_gestion");
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const startCheckout = async (planId) => {
    setBusy(true);
    try {
      const origin = window.location.origin;
      const { data } = await api.post("/billing/checkout", { origin_url: origin, plan_id: planId || selectedPlan });
      const win = window.open(data.checkout_url, "_blank", "noopener,noreferrer");
      if (!win) {
        try { (window.top || window).location.href = data.checkout_url; }
        catch { window.location.href = data.checkout_url; }
      } else {
        toast.success("Abriendo pasarela de pago…");
        const sid = data.session_id;
        const poll = setInterval(async () => {
          try {
            const { data: s } = await api.get(`/billing/checkout/status/${sid}`);
            if (s.is_active) { clearInterval(poll); toast.success("¡Pago confirmado!"); load(); }
          } catch (err) { console.warn("Billing poll", err); }
        }, 4000);
        setTimeout(() => clearInterval(poll), 10 * 60 * 1000);
      }
      setBusy(false);
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); setBusy(false); }
  };

  if (!status) return <div className="text-center py-20 text-[var(--ag-text-dim)]">Cargando…</div>;

  const meta = STATUS_LABEL[status.subscription_status] || STATUS_LABEL.none;
  const Icon = meta.icon;
  const isLifetime = status.is_lifetime;

  return (
    <div className="space-y-8 max-w-5xl">
      <header>
        <div className="text-xs uppercase tracking-[0.2em] text-[var(--ag-gold)]">Tu suscripción</div>
        <h1 className="font-display text-4xl font-black tracking-tighter mt-1">Plan & facturación</h1>
        <p className="text-sm text-[var(--ag-text-dim)] mt-2">
          {isLifetime
            ? "Tu centro tiene acceso Lifetime · gratis para siempre por configuración del propietario."
            : "Precios sin IVA. Se añade el 21 % IVA (España) al facturar. Sin permanencia, cambia o cancela cuando quieras."}
        </p>
      </header>

      {/* Estado actual */}
      <div className="ag-card p-6 lg:p-8">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-3"><AutoGlowBrand size={18} /></div>
            <div className="text-xs uppercase tracking-wider text-[var(--ag-text-dim)] mt-3">Plan actual</div>
            <div className="font-display font-bold text-2xl mt-1">{status.plan_name}</div>
            {!isLifetime && (
              <div className="flex items-baseline gap-2 mt-2">
                <span className="font-display text-4xl font-black tabular-nums">{status.plan_price_eur} €</span>
                <span className="text-sm text-[var(--ag-text-dim)]">/ mes · +21 % IVA</span>
              </div>
            )}
          </div>
          <Badge className={`${meta.tone} hover:${meta.tone} rounded-sm gap-1`} data-testid="billing-status-badge">
            <Icon size={12} weight="fill" /> {meta.label}
          </Badge>
        </div>

        <div className="mt-6 grid sm:grid-cols-3 gap-4 text-sm">
          {status.subscription_status === "trialing" && (
            <div className="ag-card p-4 border-amber-300/30">
              <div className="text-[10px] uppercase tracking-wider text-[var(--ag-gold)]">Prueba termina</div>
              <div className="text-2xl font-display font-bold tabular-nums mt-1">{fmtDate(status.trial_ends_at)}</div>
              <div className="text-xs text-[var(--ag-text-dim)] mt-1">Sin tarjeta · ningún cargo aún.</div>
            </div>
          )}
          {status.subscription_status === "active" && !isLifetime && (
            <div className="ag-card p-4 border-emerald-300/30">
              <div className="text-[10px] uppercase tracking-wider text-emerald-400">Próxima renovación</div>
              <div className="text-2xl font-display font-bold tabular-nums mt-1">{fmtDate(status.current_period_end)}</div>
              <div className="text-xs text-[var(--ag-text-dim)] mt-1">Se cargará automáticamente.</div>
            </div>
          )}
          <div className="ag-card p-4">
            <div className="text-[10px] uppercase tracking-wider text-[var(--ag-text-dim)]">Acceso</div>
            <div className="text-sm mt-1 font-medium">{status.is_active ? "Plataforma completa" : "Bloqueado — paga para reactivar"}</div>
          </div>
        </div>
      </div>

      {/* Comparativa de planes */}
      {!isLifetime && (
        <section data-testid="billing-plans">
          <div className="text-xs uppercase tracking-[0.2em] text-[var(--ag-gold)] mb-2">Planes disponibles</div>
          <h2 className="font-display text-2xl font-black tracking-tighter">Elige tu plan</h2>
          <div className="grid md:grid-cols-2 gap-5 mt-6">
            {(status.available_plans || []).map((p) => {
              const isCurrent = p.id === status.subscription_plan;
              const isFull = p.id === "ecosystem_full";
              return (
                <div key={p.id}
                  className={`ag-card p-6 relative overflow-hidden ${isFull ? "border-[var(--ag-gold)]/60" : ""}`}
                  data-testid={`plan-card-${p.id}`}>
                  {isFull && (
                    <div className="absolute top-0 right-0 bg-[var(--ag-gold)] text-[var(--ag-midnight)] text-[9px] uppercase tracking-wider font-bold px-3 py-1 rounded-bl-md">
                      Más completo
                    </div>
                  )}
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--ag-text-dim)]">{p.short}</div>
                  <div className="font-display text-2xl font-black mt-1">{p.name}</div>
                  <div className="flex items-baseline gap-1.5 mt-3">
                    <span className="font-display text-5xl font-black tabular-nums">{p.price_eur}</span>
                    <span className="text-xl text-[var(--ag-text-dim)]">€/mes</span>
                  </div>
                  <div className="text-[10px] text-[var(--ag-text-dim)]">Precios sin IVA · +21 % se añade al facturar · sin permanencia</div>
                  <ul className="mt-5 space-y-1.5 text-sm">
                    {FEATURE_LIST.map((f) => {
                      const enabled = p.features?.[f.key];
                      return (
                        <li key={f.key} className={`flex items-start gap-2 ${enabled ? "text-[var(--ag-text)]" : "text-[var(--ag-text-dim)] line-through opacity-60"}`}>
                          {enabled ? <CheckCircle size={14} weight="fill" className="text-emerald-400 shrink-0 mt-0.5" />
                            : <Warning size={14} weight="regular" className="text-red-500/60 shrink-0 mt-0.5" />}
                          <span>{f.label}</span>
                        </li>
                      );
                    })}
                  </ul>
                  <Button
                    onClick={() => startCheckout(p.id)}
                    disabled={busy}
                    className={`${isFull ? "ag-btn-gold" : "bg-[var(--ag-aqua)] text-white hover:bg-[var(--ag-aqua-soft)]"} w-full mt-6 h-11`}
                    data-testid={`plan-select-${p.id}`}>
                    <CreditCard size={14} className="mr-2" />
                    {isCurrent && status.is_active ? "Renovar" : isFull ? "Activar Ecosystem Full" : "Activar Agenda & Gestión"}
                    <ArrowRight size={14} className="ml-2" />
                  </Button>
                </div>
              );
            })}
          </div>
        </section>
      )}

      <div className="ag-card p-6 text-xs text-[var(--ag-text-dim)] leading-relaxed">
        <div className="flex items-center gap-2 mb-2 text-[var(--ag-gold)]">
          <Sparkle size={14} weight="fill" /> <span className="uppercase tracking-wider font-medium">Tu privacidad</span>
        </div>
        Tus datos están aislados por organización (multi-tenant). Solo tu equipo accede a la información del centro. Pagos procesados por Stripe — AutoGlow Ecosystem no almacena tu tarjeta.
      </div>
    </div>
  );
}
